import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { getSpecialistsForProductPage } from '@components/dashboard/data/Specialists';
import { CartItemType, DigitalServiceType, PageSlice, ProductCode } from '@contracts';
import { createCartItem } from '@utils/cart';
import { getNewProductPrice } from '@utils/price';
import AddToCart from './AddToCart';
import Colors from './Colors';
import { TextVariant, Typography } from './design/Typography';
import ReplacedText from './ReplacedText';
import SpecialistHelpsWithTags from './SpecialistHelpsWIthTags';

interface Props {
  slice: PageSlice;
}

const ConsultationsMeetNurses: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { country, currency } = state;
  const consultationProductCode = ProductCode.ConsultationSexualHealth;
  const specialistsData = getSpecialistsForProductPage(DigitalServiceType.VMS, country);
  const [productPrice, setProductPrice] = useState(-1);

  useEffect(() => {
    const fetchPrice = async () => {
      const price = await getPriceForProduct(consultationProductCode);
      setProductPrice(price !== -1 ? price : -1);
    };

    fetchPrice();
  }, [currency]);

  const getPriceForProduct = async (productCode: string) => {
    const price = await getNewProductPrice(currency, country, productCode);
    return price ?? -1;
  };

  const generateCartItem = (productCode: ProductCode) => {
    const cartItemPrice = Number(productPrice);
    const cartItemEditable = true;
    const quantity = 1;

    return createCartItem(productCode, quantity, cartItemPrice, cartItemEditable, CartItemType.Consultation, undefined, undefined, []);
  };

  return (
    <Container>
      <TextColumn>
        <ReplacedText as={Typography} $variant={TextVariant.Heading4} $mobileVariant={TextVariant.Heading6}>
          {t('free-consultations.meet-nurses-heading')}
        </ReplacedText>
        <ReplacedText as={Typography} $variant={TextVariant.BodyRegularLG} $mobileVariant={TextVariant.BodyRegularMD}>
          {t('free-consultations.meet-nurses-text')}
        </ReplacedText>
        <AddToCart cartItem={generateCartItem(consultationProductCode)} buttonText={t('free-consultations.book-consultation')} />
      </TextColumn>
      <NursesColumn>
        <ScrollContainer>
          {specialistsData.map((person, index) => {
            const firstName = person.name.split(' ')[0];
            return (
              <NurseItem key={index}>
                <NurseItemHeading>
                  <img src={person.picture} alt={person.name} />
                  <NurseItemDetails>
                    <Typography $variant={TextVariant.Heading7}>{person.name}</Typography>
                    <Typography $variant={TextVariant.UpperText5}>{person.shortDescription ?? ''}</Typography>
                  </NurseItemDetails>
                </NurseItemHeading>
                <Typography $variant={TextVariant.BodyRegularLG} $mobileVariant={TextVariant.BodyRegularMD}>
                  {person.bio}
                </Typography>
                {person.helpWithTags ? <SpecialistHelpsWithTags name={firstName} tags={person.helpWithTags} /> : null}
              </NurseItem>
            );
          })}
        </ScrollContainer>
      </NursesColumn>
    </Container>
  );
};

export default ConsultationsMeetNurses;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  padding: 96px 120px;
  background: ${Colors.babyBlue};
  width: 100%;

  @media (max-width: 1300px) {
    flex-direction: column;
    align-items: center;
    gap: 40px;
    padding: 80px;
  }

  @media (max-width: 840px) {
    padding: 56px 0 36px;
  }
`;

const TextColumn = styled.div`
  display: flex;
  width: 100%;
  max-width: 420px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 0 30px;

  @media (max-width: 1300px) {
    align-items: center;
  }

  @media (max-width: 768px) {
    align-items: flex-start;
  }

  & h4 {
    font-family: 'Domaine';
    font-size: 40px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: -0.4px;
    color: ${Colors.navyBlue};

    @media (max-width: 840px) {
      font-size: 24px;
    }
  }

  & span {
    color: ${Colors.navyBlue};

    @media (max-width: 1300px) {
      text-align: center;
    }
  }

  & button {
    margin-top: 8px;

    @media (max-width: 840px) {
      padding: 0 10px;
      width: 100%;
    }
  }
`;

const NursesColumn = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: flex-end;

  @media (max-width: 1300px) {
    justify-content: center;
  }

  @media (max-width: 840px) {
    justify-content: flex-start;
    overflow-x: scroll;
    padding-bottom: 20px;
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 32px;
  align-items: flex-start;
  justify-content: flex-end;

  @media (max-width: 1300px) {
    justify-content: center;
  }

  @media (max-width: 840px) {
    padding: 0 30px;
  }
`;

const NurseItem = styled.div`
  display: flex;
  flex-grow: 1;
  width: 360px;
  padding: 30px;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  border-radius: 5px;
  border: 2px solid ${Colors.sky};
  background: ${Colors.cream};

  @media (max-width: 840px) {
    width: 320px;
    max-width: calc(100vw - 80px);
  }

  & .help-with-heading {
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
  }

  & .help-with-tag {
    background: ${Colors.babyBlue};
  }
`;

const NurseItemHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  & img {
    max-width: 60px;
  }
`;

const NurseItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
`;
